<template>
  <v-form
    ref="recurrenceForm"
    v-model="isFormValid"
    :disabled="disabled || loading"
  >
    <div class="d-flex flex-column" :style="{ gap: '12px' }">
      <p class="ma-0 text-left font-weight-bold">Modificar Recurrencia</p>

      <v-select
        v-model="recurrence.type"
        label="Repetición"
        filled
        hide-details="auto"
        :items="RecurrenceTypeOptions"
        item-text="text"
        item-value="value"
      />

      <template v-if="isRecurrenceEnabled">
        <v-row v-if="showInterval" align="center">
          <v-col cols="6">
            <label class="w-100 text-left font-weight-medium">
              Repetir cada
            </label>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="recurrence.interval"
              filled
              hide-details="auto"
              :min="1"
              :suffix="intervalSuffix"
              type="number"
            />
          </v-col>
        </v-row>

        <div v-if="showByDay">
          <label class="w-100 text-left mb-3 font-weight-medium">
            Repetir el
          </label>
          <div class="d-flex justify-space-between">
            <div
              v-for="{ id, letter } in RECURRENCE_DAYS"
              :key="id"
              :class="[
                'day-circle',
                { 'day-circle-active': recurrence.byDay.includes(id) },
                { 'day-circle-disabled': disabled || loading },
              ]"
              @click="!loading && toggleRecurrenceDay(id)"
            >
              {{ letter }}
            </div>
          </div>
        </div>

        <div>
          <label class="w-100 text-left font-weight-medium">Finaliza</label>
          <v-radio-group v-model="recurrence.endType" class="ma-0">
            <v-radio
              :label="RecurrenceEndTypeLabels[RecurrenceEndType.NEVER]"
              :value="RecurrenceEndType.NEVER"
            />

            <v-radio :value="RecurrenceEndType.UNTIL">
              <template #label>
                <v-row align="center">
                  <v-col cols="4">
                    <span>
                      {{ RecurrenceEndTypeLabels[RecurrenceEndType.UNTIL] }}
                    </span>
                  </v-col>
                  <v-col cols="8">
                    <v-menu
                      v-model="isRecurrenceEndDatePickerOpen"
                      :close-on-content-click="false"
                      content-class="white"
                      offset-y
                    >
                      <template #activator="{ on }">
                        <v-text-field
                          :value="formatDate(recurrence.until)"
                          :disabled="
                            recurrence.endType !== RecurrenceEndType.UNTIL
                          "
                          filled
                          hide-details="auto"
                          readonly
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="recurrence.until"
                        color="red"
                        mode="date"
                        :min-date="event.parentStartDate ?? event.startDate"
                        @input="isRecurrenceEndDatePickerOpen = false"
                      />
                    </v-menu>
                  </v-col>
                </v-row>
              </template>
            </v-radio>

            <v-radio :value="RecurrenceEndType.COUNT">
              <template #label>
                <v-row align="center">
                  <v-col cols="4">
                    <span>
                      {{ RecurrenceEndTypeLabels[RecurrenceEndType.COUNT] }}
                    </span>
                  </v-col>
                  <v-col cols="8">
                    <v-text-field
                      v-model.number="recurrence.count"
                      :disabled="recurrence.endType !== RecurrenceEndType.COUNT"
                      filled
                      hide-details="auto"
                      :min="2"
                      suffix="repeticiones"
                      type="number"
                    />
                  </v-col>
                </v-row>
              </template>
            </v-radio>
          </v-radio-group>
        </div>
      </template>

      <!-- LOCATION -->
      <v-combobox
        v-if="availableLocations.length"
        :value="recurrenceLocation"
        label="Ubicación"
        clearable
        filled
        hide-details="auto"
        hide-no-data
        hide-selected
        :items="availableLocations"
        :rules="[rules.required]"
        @change="
          recurrenceLocation =
            typeof $event === 'object' ? $event.value : $event
        "
        @click:clear="recurrenceLocation = null"
      />
      <v-text-field
        v-else
        v-model="recurrenceLocation"
        label="Ubicación"
        clearable
        filled
        hide-details="auto"
        :rules="[rules.required]"
        @click:clear="recurrenceLocation = null"
      />

      <v-btn
        :color="isRecurrenceValid && !disabled ? '#E20809' : ''"
        :dark="isRecurrenceValid && !disabled"
        :elevation="0"
        :disabled="!isRecurrenceValid || disabled"
        :loading="loading"
        @click="onUpdateRecurrence"
      >
        Guardar
      </v-btn>
    </div>
  </v-form>
</template>

<script>
  import useRecurrence from '@/mixins/schedule/useRecurrence';
  import {
    RecurrenceTypeOptions,
    RecurrenceEndType,
    RecurrenceEndTypeLabels,
  } from '@/core/constants/schedule/recurrence';
  import moment from 'moment';

  export default {
    name: 'EventDetailsRecurrenceForm',
    mixins: [useRecurrence],
    props: {
      availableLocations: { type: Array, default: () => [] },
      disabled: { type: Boolean, default: false },
      event: { type: Object, required: true },
      loading: { type: Boolean, default: false },
    },
    data: () => ({
      isFormValid: false,
      recurrenceLocation: null,
      RecurrenceTypeOptions,
      RecurrenceEndType,
      RecurrenceEndTypeLabels,
      rules: {
        required: (v) => !!v || 'Campo requerido',
      },
    }),
    watch: {
      event: {
        immediate: true,
        handler(event) {
          if (!event) return;
          this.loadRecurrence(event);
          this.recurrenceLocation = event.location || null;
        },
      },
    },
    methods: {
      formatDate(date) {
        if (!date) return '';
        return moment(date).format('ddd D [de] MMM [del] YYYY');
      },
      onUpdateRecurrence() {
        this.$emit('onUpdateRecurrence', {
          ...this.recurrencePayload,
          location: this.recurrenceLocation,
        });
      },
    },
  };
</script>

<style scoped>
  ::v-deep .vc-container {
    border: none;
    width: 100%;
  }

  ::v-deep .v-list-item__title,
  .v-list-item__subtitle {
    text-align: left;
  }

  .day-circle {
    align-items: center;
    background-color: #f2f2f2;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    font-size: 13px;
    font-weight: 500;
    height: 36px;
    justify-content: center;
    user-select: none;
    width: 36px;
  }

  .day-circle-active {
    background-color: #e20809;
    color: white;
  }

  .day-circle-disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
</style>
